import { useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, Boxes } from "lucide-react";

/**
 * Modal to assign platforms (Idealista Pro, Confidencial Imobiliário, Fizbot...)
 * to a single consultant. Allowed for admin OR staff with is_platforms_manager.
 * The parent unmounts/remounts this modal per player click, so initial state from
 * props is enough — no effect-based sync needed.
 */
export default function PlatformAssignModal({ player, platforms, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(player.platform_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/players/${player.id}/platforms`, { platform_ids: Array.from(selected) });
      toast.success("Plataformas atualizadas");
      onSaved && onSaved(Array.from(selected));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="kw-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="platform-assign-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="kw-overline">Plataformas de</div>
            <h3 className="font-display text-xl font-black uppercase tracking-tight">{player.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" aria-label="Fechar" data-testid="platform-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {platforms.length === 0 ? (
          <p className="text-sm text-[#737373] italic">
            Sem plataformas configuradas. Cria-as em <span className="font-mono">/settings</span>.
          </p>
        ) : (
          <div className="space-y-2 mb-5">
            {platforms.map((p) => {
              const isOn = selected.has(p.id);
              return (
                <label key={p.id}
                  className={`flex items-center gap-3 border p-2 cursor-pointer transition-colors ${
                    isOn ? "border-[#E50000] bg-[#E50000]/5" : "border-[#222] hover:border-[#444]"
                  }`}
                  data-testid={`platform-assign-${p.id}`}>
                  <input type="checkbox" className="accent-[#E50000]" checked={isOn} onChange={() => toggle(p.id)} />
                  <div className="w-8 h-8 flex items-center justify-center border border-[#222] bg-[#0a0a0a]">
                    {p.logo_url ? (
                      <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Boxes className="w-4 h-4" style={{ color: p.color }} />
                    )}
                  </div>
                  <div className="flex-1 font-display font-bold uppercase tracking-tight" style={{ color: isOn ? p.color : undefined }}>
                    {p.name}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="kw-btn-secondary" data-testid="platform-modal-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="kw-btn-primary" data-testid="platform-modal-save">
            {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
